import { del, get, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"

export const ARCHIVED_PROJECTS_PAGE_SIZE = 10

export const ARCHIVED_SORT_OPTIONS = [
  { value: "created_at", label: "Archived At" },
  { value: "name", label: "Project Name" },
  { value: "start_date", label: "Start Date" },
  { value: "end_date", label: "End Date" },
  { value: "updated_at", label: "Updated At" },
]

const ARCHIVE_LIST_WITH = ["project.categories", "project.latest_members.user"]

const formatDate = dateString => {
  if (!dateString) return "—"
  const date = new Date(dateString)
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
}

const getProjectCode = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—"

const extractCategories = project => {
  if (project?.category) return [project.category]
  if (Array.isArray(project?.categories) && project.categories.length > 0) {
    return project.categories
      .map(cat => (typeof cat === "string" ? cat : cat?.name || ""))
      .filter(Boolean)
  }
  return []
}

const memberDisplayName = member => {
  const user = member?.user || member || {}
  return getUserFullName(user, {
    fallback: getUserFullName(member, { fallback: "Collaborator" }),
  })
}

const mapCollaborators = (row, project) => {
  const members =
    project?.latest_members ||
    row?.members ||
    project?.members ||
    project?.latest_3_active_collabs ||
    []
  return (Array.isArray(members) ? members : []).map(member => ({
    name: memberDisplayName(member),
    src: getUserAvatarUrl(member?.user || member),
  }))
}

const getDaysRemaining = endDateString => {
  if (!endDateString) return "—"
  const end = new Date(endDateString)
  const now = new Date()
  const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
  return Number.isNaN(days) ? "—" : `${Math.max(0, days)} Days`
}

/**
 * Map GET /api/v1/project-backups/archive row → table model.
 * `id` is the project-member backup id used by restore / permanent APIs.
 */
export const mapArchivedProject = row => {
  const project =
    row?.project && typeof row.project === "object" ? row.project : row
  const backupId = row?.id
  if (!backupId) return null

  return {
    id: backupId,
    projectId: row?.project_id || project?.id || null,
    code: getProjectCode(project?.name),
    title: project?.name || "—",
    categories: extractCategories(project),
    collaborators: mapCollaborators(row, project),
    status: "archived",
    daysRemaining: getDaysRemaining(project?.end_date),
    startDate: formatDate(project?.start_date),
    endDate: formatDate(project?.end_date),
    archivedDate: formatDate(
      row?.deleted_at ||
        project?.archived_at ||
        project?.deleted_at ||
        row?.updated_at ||
        project?.updated_at,
    ),
    raw: row,
  }
}

const emptyPage = (page = 1) => ({
  ok: true,
  list: [],
  total: 0,
  page,
  lastPage: 1,
  perPage: ARCHIVED_PROJECTS_PAGE_SIZE,
})

const unwrapPagedList = result => {
  if (Array.isArray(result)) {
    return {
      list: result,
      total: result.length,
      page: 1,
      lastPage: 1,
      perPage: result.length || ARCHIVED_PROJECTS_PAGE_SIZE,
    }
  }
  const list = Array.isArray(result?.data) ? result.data : []
  return {
    list,
    total: Number(result?.total) || list.length,
    page: Number(result?.current_page) || 1,
    lastPage: Number(result?.last_page) || 1,
    perPage:
      Number(result?.per_page || result?.page_size) ||
      ARCHIVED_PROJECTS_PAGE_SIZE,
  }
}

const isAbortError = err =>
  Boolean(
    err?.name === "CanceledError" ||
      err?.code === "ERR_CANCELED" ||
      err?.name === "AbortError",
  )

/**
 * GET /api/v1/project-backups/archive
 */
export const fetchArchivedProjects = async ({
  page = 1,
  pageSize = ARCHIVED_PROJECTS_PAGE_SIZE,
  search = "",
  categoryIds = [],
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
} = {}) => {
  try {
    const params = {
      search: search || undefined,
      page,
      page_size: pageSize,
      sort_by: sortBy || "created_at",
      sort_direction: sortDirection || "desc",
      "with[]": ARCHIVE_LIST_WITH,
    }
    if (categoryIds.length > 0) {
      params["category_ids[]"] = categoryIds
    }

    const response = await get(endpoints.getArchivedProjects, {
      params,
      signal,
      skipErrorToast: true,
    })

    if (signal?.aborted) return { ok: false, aborted: true, ...emptyPage(page) }
    if (!response?.data?.success) {
      toastApiError(response, "Failed to load archived projects")
      return { ok: false, ...emptyPage(page) }
    }

    const pageData = unwrapPagedList(response.data.result)
    return {
      ok: true,
      list: pageData.list.map(mapArchivedProject).filter(Boolean),
      total: pageData.total,
      page: pageData.page,
      lastPage: pageData.lastPage,
      perPage: pageData.perPage,
    }
  } catch (err) {
    if (signal?.aborted || isAbortError(err)) {
      return { ok: false, aborted: true, ...emptyPage(page) }
    }
    toastApiError(err, "Failed to load archived projects")
    return { ok: false, ...emptyPage(page) }
  }
}

/**
 * POST /api/v1/project-backups/:projectMemberId/restore
 */
export const restoreArchivedProject = async projectMemberId => {
  if (!projectMemberId) return { ok: false }

  try {
    const response = await post(
      endpoints.restoreArchivedProject(projectMemberId),
      {},
      { skipErrorToast: true },
    )
    if (!response?.data?.success) return { ok: false, response }
    return { ok: true, response }
  } catch (err) {
    return { ok: false, response: err?.response || err }
  }
}

/**
 * DELETE /api/v1/project-backups/:projectMemberId/permanent
 */
export const permanentlyDeleteArchivedProject = async projectMemberId => {
  if (!projectMemberId) return { ok: false }

  try {
    const response = await del(
      endpoints.permanentlyDeleteArchivedProject(projectMemberId),
      { skipErrorToast: true },
    )
    if (!response?.data?.success) return { ok: false, response }
    return { ok: true, response }
  } catch (err) {
    return { ok: false, response: err?.response || err }
  }
}

/** @deprecated Use restoreArchivedProject */
export const reactivateArchivedProject = restoreArchivedProject
